import React, { useEffect, useRef, useState } from "react";
import RecordingManager from "../recording/RecordingManager";
import { getAssignmentDownloadUrl } from "../../services/playtesterService";
import "./SteamGameLauncher.scss";

function DownloadableGameLauncher({ assignment, onClose, onError }) {
  const recordingManagerRef = useRef(null);
  const screenCaptureStreamRef = useRef(null);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingStatus, setRecordingStatus] = useState("idle");
  const [localError, setLocalError] = useState(null);

  const resolveBuildDownloadUrl = (assignmentValue) => {
    const candidates = [
      assignmentValue?.buildFileUrl,
      assignmentValue?.buildFileURL,
      assignmentValue?.buildUrl,
      assignmentValue?.downloadUrl,
      assignmentValue?.fileUrl,
      assignmentValue?.game?.buildFileUrl,
      assignmentValue?.game?.buildFileURL,
      assignmentValue?.game?.downloadUrl,
      assignmentValue?.gameDetails?.buildFileUrl,
      assignmentValue?.gameDetails?.buildFileURL,
    ];

    const firstValid = candidates.find(
      (value) =>
        typeof value === "string" &&
        value.trim() &&
        value.trim() !== "null" &&
        value.trim() !== "undefined"
    );

    return firstValid ? firstValid.trim() : "";
  };

  const buildFileUrl = resolveBuildDownloadUrl(assignment);

  const notifyError = (message) => {
    setLocalError(message);
    if (onError) {
      onError(message);
    }
  };

  const openDownload = async () => {
    const assignmentId = assignment?.assignmentId || assignment?._id || assignment?.id;
    console.info("[DownloadableGameLauncher] Download click", {
      assignmentId,
      gameId: assignment?.gameId,
      buildType: assignment?.buildType,
      resolvedBuildFileUrl: buildFileUrl,
    });

    const signedDownloadUrl = await getAssignmentDownloadUrl(assignmentId);
    if (signedDownloadUrl) {
      window.location.href = signedDownloadUrl;
      return;
    }

    if (!buildFileUrl) {
      console.error("[DownloadableGameLauncher] Missing build download URL on click", assignment);
      notifyError("Build download URL is missing for this assignment.");
      return;
    }
    // Fallback for environments where download endpoint isn't available yet.
    window.location.href = buildFileUrl;
  };

  const clearScreenCaptureStream = () => {
    if (screenCaptureStreamRef.current) {
      screenCaptureStreamRef.current.getTracks().forEach((track) => track.stop());
      screenCaptureStreamRef.current = null;
    }
  };

  const ensureScreenCapturePermission = async () => {
    if (screenCaptureStreamRef.current) {
      const track = screenCaptureStreamRef.current.getVideoTracks?.()[0];
      if (track && track.readyState === "live") {
        return;
      }
      clearScreenCaptureStream();
    }

    const stream = await navigator.mediaDevices.getDisplayMedia({
      video: {
        mediaSource: "screen",
        width: { ideal: 1920 },
        height: { ideal: 1080 },
      },
      audio: {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
      },
    });

    screenCaptureStreamRef.current = stream;

    const videoTrack = stream.getVideoTracks?.()[0];
    if (videoTrack) {
      videoTrack.onended = () => {
        clearScreenCaptureStream();
      };
    }
  };

  const startSession = async () => {
    if (!recordingManagerRef.current || isRecording || recordingStatus === "starting") {
      return;
    }

    setLocalError(null);
    setRecordingStatus("starting");
    try {
      await ensureScreenCapturePermission();
      await recordingManagerRef.current.start();
      setRecordingStatus("recording");
      setIsRecording(true);
    } catch (err) {
      const message = err?.message || "Failed to start recording.";
      setRecordingStatus("error");
      notifyError(message);
    }
  };

  const stopSession = async () => {
    if (
      !recordingManagerRef.current ||
      !isRecording ||
      recordingStatus === "stopping"
    ) {
      return;
    }

    setRecordingStatus("stopping");
    try {
      await recordingManagerRef.current.stop();
      setIsRecording(false);
      setRecordingStatus("stopped");
      clearScreenCaptureStream();
    } catch (err) {
      const message = err?.message || "Failed to stop recording.";
      setRecordingStatus("error");
      notifyError(message);
    }
  };

  const handleClose = async () => {
    try {
      if (isRecording) {
        await stopSession();
      }
    } finally {
      if (onClose) {
        onClose();
      }
    }
  };

  useEffect(() => {
    return () => {
      if (recordingManagerRef.current) {
        recordingManagerRef.current.stop().catch(() => {});
      }
      clearScreenCaptureStream();
    };
  }, []);

  return (
    <div className="steam-game-launcher">
      <RecordingManager
        ref={recordingManagerRef}
        assignmentId={assignment.assignmentId || assignment._id}
        gameId={assignment.gameId}
        username={assignment.username}
        getScreenCaptureStream={() => screenCaptureStreamRef.current}
        onRecordingStarted={() => {
          setIsRecording(true);
          setRecordingStatus("recording");
        }}
        onRecordingStopped={() => {
          setIsRecording(false);
          setRecordingStatus("stopped");
        }}
        onError={(message) => {
          setRecordingStatus("error");
          notifyError(message);
        }}
      />

      <div className="steam-launcher-panel">
        <h3>{assignment?.gameName || "Downloadable Game"} Session</h3>
        <p className="steam-launcher-subtitle">
          Download and install the build first, then start recording before you play.
        </p>

        <div className="steam-instructions">
          <p>1. Click Download Build and install the game.</p>
          <p>2. Return here and click Start Recording.</p>
          <p>3. In screen share picker, choose Entire Screen or a Window.</p>
          <p>4. Play your installed game as normal.</p>
          <p>5. Return here and click End Session when done.</p>
        </div>

        <div className="steam-status-row">
          <span className={`status-dot ${isRecording ? "recording" : ""}`} />
          <span className="status-text">
            {isRecording
              ? "Recording"
              : recordingStatus === "starting"
              ? "Starting..."
              : recordingStatus === "stopping"
              ? "Stopping..."
              : "Not Recording"}
          </span>
        </div>

        {localError && <div className="steam-error">{localError}</div>}

        <div className="steam-actions">
          <button className="btn btn-secondary" onClick={openDownload}>
            Download Build
          </button>

          {!isRecording ? (
            <button
              className="btn btn-primary"
              onClick={startSession}
              disabled={recordingStatus === "starting"}
            >
              Start Recording
            </button>
          ) : (
            <button
              className="btn btn-danger"
              onClick={stopSession}
              disabled={recordingStatus === "stopping"}
            >
              {recordingStatus === "stopping" ? "Ending Session..." : "End Session"}
            </button>
          )}

          <button className="btn btn-secondary" onClick={handleClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export default DownloadableGameLauncher;
